/**
 * Página de Agendamento Online - Configuração admin
 * 
 * Permite gerenciar tipos de eventos, disponibilidade semanal,
 * exceções de data e integração com Google Calendar.
 * 
 * RELEASE: 2026-10-09 v1.0.1 - Cabeçalho padronizado: removidos ícone à frente do
 *                              título e subtítulo descritivo (padrão das demais páginas).
 * RELEASE: 2026-02-14 v1.0.0 - Versão inicial.
 * VERSION: 1.0.1
 */

import { useState } from 'react';
import { Plus, Calendar, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  SchedulingEventTypesList,
  CreateEventTypeDialog,
  EventTypeEditor,
  CalendarIntegrationsModal,
  OnlineSchedulingKpis,
} from '@/components/tenant/scheduling';
import { ReminderRulesManager } from '@/components/reminders/ReminderRulesManager';
import { type SchedulingEventType } from '@/hooks/useSchedulingEventTypes';

const TenantScheduling = () => {
  // Controla se está na listagem ou editando um event type
  const [selectedEventType, setSelectedEventType] = useState<SchedulingEventType | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showCalendarsModal, setShowCalendarsModal] = useState(false);
  const [showRemindersModal, setShowRemindersModal] = useState(false);

  // Se há um event type selecionado, exibe o editor
  if (selectedEventType) {
    return (
      <div className="space-y-6">
        <EventTypeEditor
          eventType={selectedEventType}
          onBack={() => setSelectedEventType(null)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cabeçalho padronizado: somente título + ações, sem ícone e sem subtítulo */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-foreground">Agendamento Online</h2>
        <div className="flex items-center gap-2">
          <Button size="xs" variant="outline" onClick={() => setShowRemindersModal(true)} className="hover:shadow-md hover:scale-[1.02] transition-all duration-200">
            <Bell className="h-4 w-4" />
            Lembretes
          </Button>
          <Button size="xs" variant="outline" onClick={() => setShowCalendarsModal(true)} className="hover:shadow-md hover:scale-[1.02] transition-all duration-200">
            <Calendar className="h-4 w-4" />
            Calendários
          </Button>
          <Button size="xs" onClick={() => setShowCreateDialog(true)} className="hover:shadow-md hover:scale-[1.02] transition-all duration-200">
            <Plus className="h-4 w-4" />
            Novo Evento
          </Button>
        </div>
      </div>

      {/* KPIs e Gráfico Evolutivo */}
      <OnlineSchedulingKpis />

      {/* Lista de Event Types */}
      <SchedulingEventTypesList
        onEdit={setSelectedEventType}
        onCreate={() => setShowCreateDialog(true)}
      />

      {/* Dialog de criação */}
      <CreateEventTypeDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />

      {/* Modal de gerenciamento de calendários */}
      <CalendarIntegrationsModal
        open={showCalendarsModal}
        onOpenChange={setShowCalendarsModal}
      />

      {/* Modal global de lembretes de agendamento */}
      <Dialog open={showRemindersModal} onOpenChange={setShowRemindersModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-primary" />
              Lembretes de Agendamento
            </DialogTitle>
          </DialogHeader>
          <ReminderRulesManager
            entityType="scheduling"
            title="Regras de Lembrete"
            description="Configure lembretes automáticos enviados antes dos eventos agendados. Aplicável a todos os tipos de evento."
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantScheduling;
